import { z } from "zod";
import { hasControlCharacter, hasLoneSurrogate, ID_PATTERN, idSchema } from "./text.js";
// La definizione dei filtri dell'elenco Lead e delle Smart List, versione 1 (piano M3, D1; `CRM_DATA_DICTIONARY.md`
// §8.2). Una radice con il suo combinatore («tutte» o «almeno una») e al massimo un livello di gruppi, come la regola
// confermata «al massimo due livelli». Ogni condizione è tipizzata dal campo: una scelta fra voci, i tag, un numero,
// una data, da M5 la prossima task e, dall'ingresso dei Lead (I5), i clic sulle email. Il contratto ferma la forma; il server controlla poi, per l'azienda, che un campo `field:<chiave>` esista e
// sia del tipo della condizione, e risponde `422 filter_invalid` con `details.field`.
export const FILTER_CHOICE_OPERATORS = ["in", "not_in"];
export const FILTER_TAG_OPERATORS = ["any", "all", "none"];
export const FILTER_NUMBER_OPERATORS = ["gte", "lte", "eq"];
export const FILTER_DATE_OPERATORS = ["today", "yesterday", "last7", "last30", "older7", "older30", "older90", "older180", "older365", "on", "before", "after"];
/** Gli operatori di data che chiedono un giorno (`day`): «il», «prima del» e «dopo il». */
export const FILTER_DAY_OPERATORS = ["on", "before", "after"];
/**
 * Gli operatori di «Prossima task» (piano M5, Task 20; decisione del 25 settembre 2026, mock-up `OPERATORS.task`): «scaduta»,
 * «oggi» (più tardi, oggi), «domani», «entro il giorno…» (con `day`, le scadute comprese) e «nessuna». Guardano soltanto il
 * richiamo aperto del Lead (questione 264): le chiamate della sequenza non sono Task.
 */
export const FILTER_TASK_OPERATORS = ["overdue", "today", "tomorrow", "by", "none"];
/** L'operatore di «Prossima task» che chiede un giorno (`day`): «entro il giorno…». */
export const FILTER_TASK_DAY_OPERATORS = ["by"];
/**
 * Gli operatori di «Ha cliccato un'email» (piano dell'ingresso del 12 ottobre 2026, I5, D5; INGR-22): «negli ultimi N giorni» (con
 * `days`, da 1 a 365: oggi e gli N − 1 giorni prima, nel fuso dell'azienda) e «mai». Guardano soltanto i clic: le aperture possono
 * essere anteprime automatiche.
 */
export const FILTER_EMAIL_OPERATORS = ["within", "never"];
/** I giorni di «negli ultimi N giorni»: al più un anno; l'interfaccia parte da 7. */
export const FILTER_EMAIL_DAYS = { min: 1, max: 365, default: 7 };
/**
 * Le voci del filtro «Sequenza di chiamate» (piano M5, Task 14; `DECISIONS.md`, sequenza, novità 16), nell'ordine del mock-up:
 * «Da chiamare oggi» (anche in ritardo), «Lead nuovo, mai chiamato», «In corso, più avanti», «Finita o ferma». Un Lead può stare
 * in due voci insieme: un Lead nuovo è anche da chiamare oggi.
 */
export const SEQUENCE_FILTER_VALUES = ["due_today", "new_never_called", "in_progress_later", "ended"];
/**
 * Le voci del filtro «Opportunità» (piano M6, R8, Task 24; questione 141; LEAD-20), nell'ordine del mock-up: «Nessuna», poi le fasi
 * di SEZ-9 («Disco prenotata», «Disco effettuata», «Demo prenotata», «Demo effettuata», «Club o Percorso venduto», «Persa»). La
 * fase di un Lead è quella della sua Opportunità aperta o, senza, dell'ultima chiusa; «Nessuna» è un Lead che non ne ha mai avuta una.
 */
export const OPPORTUNITY_FILTER_VALUES = ["none", "disco_booked", "disco_held", "demo_booked", "demo_held", "sold", "lost"];
/**
 * I campi di sistema filtrabili, nell'ordine del pannello (mock-up `FIELDS`); i campi dell'azienda sono `field:<chiave>`. Da M5
 * «Prossima task» (`nextTaskAt`, Task 20) e «Sequenza di chiamate» (`sequence`, Task 14), in testa a «Chiamate e task»; dall'ingresso
 * dei Lead (I5) «Ha cliccato un'email» (`emailClicked`) nel gruppo «Provenienza»; da R8 di M6 «Opportunità» (`opportunity`, Task 24)
 * nel gruppo suo, «Disco, demo e opportunità», fra «Salone» e «Provenienza» come nel mock-up.
 */
export const SYSTEM_FILTER_FIELDS = {
    owner: { type: "choice", group: "owner" },
    status: { type: "choice", group: "status" },
    tags: { type: "tags", group: "status" },
    nextTaskAt: { type: "task", group: "calls" },
    sequence: { type: "choice", group: "calls" },
    latestCallAt: { type: "date", group: "calls" },
    latestCallOutcome: { type: "choice", group: "calls" },
    outgoingCallCount: { type: "number", group: "calls" },
    outgoingCallCountLast12Months: { type: "number", group: "calls" },
    outgoingCallCountLast4Months: { type: "number", group: "calls" },
    country: { type: "choice", group: "territory" },
    region: { type: "choice", group: "territory" },
    province: { type: "choice", group: "territory" },
    city: { type: "choice", group: "territory" },
    opportunity: { type: "choice", group: "opportunities" },
    createdAt: { type: "date", group: "created" },
    emailClicked: { type: "email", group: "created" },
};
export const COMPANY_FIELD_PREFIX = "field:";
// Soltanto le chiavi proprie del catalogo: `in` direbbe di sì anche a `toString` o `constructor`.
const isSystemFilterField = (ref) => Object.hasOwn(SYSTEM_FILTER_FIELDS, ref);
/** `owner`, `status`, … oppure `field:<chiave>` (chiave con la forma di `ID_PATTERN`). */
export const filterFieldRefSchema = z
    .string()
    .max(80)
    .refine((ref) => isSystemFilterField(ref) || (ref.startsWith(COMPANY_FIELD_PREFIX) && ID_PATTERN.test(ref.slice(COMPANY_FIELD_PREFIX.length))), { message: "Unknown filter field" });
/**
 * Una voce di un filtro a scelta: un identificativo, un codice o un nome del catalogo (`Forlì-Cesena`). È l'unico testo
 * libero della definizione: arriva come escape JSON dentro il parametro `filter` e il server la passa come parametro SQL,
 * dove un NUL fa rispondere PostgreSQL con un errore (SQLSTATE 22021) e una metà di surrogato non è un carattere. I
 * caratteri di controllo e le metà sole si fermano qui, come negli altri testi (`text.ts`), e il filtro è un 422.
 */
const filterChoiceValueSchema = z
    .string()
    .min(1)
    .max(80)
    .refine((value) => !hasControlCharacter(value) && !hasLoneSurrogate(value), { message: "Control characters and unpaired surrogates are not allowed" });
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Gli anni di un giorno: dal 1900 al 9998. Gli estremi di un giorno, nel fuso dell'azienda, sono istanti che PostgreSQL deve
 * rappresentare, anche quello del giorno dopo (la fine di «il», «dopo il» e di «Personalizzato»): con il fuso di Roma il 1°
 * gennaio dell'anno 1 comincia nell'anno 0, e il giorno dopo il 31 dicembre 9999 è nell'anno 10000, che il database rifiuta.
 * Un periodo o un filtro estremo resta così un `422`, mai un errore del server (revisione del Task 18 del piano M4); per
 * cercare prima del 1900 basta «prima del 01/01/1900».
 */
const ISO_DAY_YEARS = { min: 1900, max: 9998 };
/**
 * Un giorno `AAAA-MM-GG` che il calendario ha, fra gli anni 1900 e 9998: il 30 febbraio, il mese 13 o l'anno 1 si fermano qui,
 * prima che il server calcoli gli estremi del giorno (`dayBounds` di `@gioia365/domain`, che per un giorno impossibile lancia
 * `RangeError`).
 */
export const isoDaySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((day) => {
    const match = ISO_DAY.exec(day);
    if (!match)
        return true; // la forma la segnala già `regex`
    const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (year < ISO_DAY_YEARS.min || year > ISO_DAY_YEARS.max)
        return false;
    // Un giorno che il calendario non ha scivola in un altro: il 30 febbraio diventa il 2 marzo.
    const utc = new Date(0);
    utc.setUTCFullYear(year, month - 1, date);
    return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === date;
}, { message: "Day does not exist or is outside the years 1900–9998" });
export const filterConditionSchema = z
    .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("choice"), field: filterFieldRefSchema, op: z.enum(FILTER_CHOICE_OPERATORS), values: z.array(filterChoiceValueSchema).min(1).max(200) }),
    z.strictObject({ kind: z.literal("tags"), field: z.literal("tags"), op: z.enum(FILTER_TAG_OPERATORS), values: z.array(idSchema).min(1).max(200) }),
    z.strictObject({ kind: z.literal("number"), field: filterFieldRefSchema, op: z.enum(FILTER_NUMBER_OPERATORS), value: z.number().int().min(0).max(1_000_000) }),
    z.strictObject({ kind: z.literal("date"), field: filterFieldRefSchema, op: z.enum(FILTER_DATE_OPERATORS), day: isoDaySchema.optional() }),
    z.strictObject({ kind: z.literal("task"), field: z.literal("nextTaskAt"), op: z.enum(FILTER_TASK_OPERATORS), day: isoDaySchema.optional() }),
    z.strictObject({
        kind: z.literal("email"),
        field: z.literal("emailClicked"),
        op: z.enum(FILTER_EMAIL_OPERATORS),
        days: z.number().int().min(FILTER_EMAIL_DAYS.min).max(FILTER_EMAIL_DAYS.max).optional(),
    }),
])
    .refine((condition) => condition.kind !== "date" || FILTER_DAY_OPERATORS.includes(condition.op) === (condition.day !== undefined), {
    message: "day is required exactly for on/before/after",
})
    .refine((condition) => condition.kind !== "task" || FILTER_TASK_DAY_OPERATORS.includes(condition.op) === (condition.day !== undefined), {
    message: "day is required exactly for by",
})
    .refine((condition) => condition.kind !== "email" || (condition.op === "within") === (condition.days !== undefined), {
    message: "days is required exactly for within",
});
/** Un gruppo della radice: le sue condizioni con il suo combinatore, senza altri gruppi dentro. */
export const filterGroupSchema = z.strictObject({ combinator: z.enum(["all", "any"]), conditions: z.array(filterConditionSchema).min(1).max(50) });
export const filterDefinitionSchema = z.strictObject({
    version: z.literal(1),
    combinator: z.enum(["all", "any"]).default("all"),
    conditions: z.array(filterConditionSchema).max(50).default([]),
    groups: z.array(filterGroupSchema).max(10).default([]),
});
export const EMPTY_FILTER = { version: 1, combinator: "all", conditions: [], groups: [] };
export const FILTER_JSON_MAX_LENGTH = 4000;
/** Il parametro `filter` di `GET /api/leads`: un JSON di al massimo 4.000 caratteri; una forma sbagliata è `422 validation_failed` sul campo `filter`. */
export const filterJsonSchema = z
    .string()
    .max(FILTER_JSON_MAX_LENGTH)
    .transform((raw, ctx) => {
    try {
        return JSON.parse(raw);
    }
    catch {
        ctx.addIssue({ code: "custom", message: "filter is not JSON" });
        return z.NEVER;
    }
})
    .pipe(filterDefinitionSchema);
/** Vero per la definizione che non restringe nulla: nessuna condizione e nessun gruppo. */
export function isEmptyFilter(definition) {
    return definition.conditions.length === 0 && definition.groups.length === 0;
}
/** Il tipo di una condizione per il suo campo di sistema; `null` per un campo dell'azienda (lo decide il server dalla definizione del campo). */
export function systemFilterType(field) {
    return isSystemFilterField(field) ? SYSTEM_FILTER_FIELDS[field].type : null;
}
//# sourceMappingURL=filters.js.map